import React, { useEffect, useMemo, useState, useCallback } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, dateFmt, dateTimeFmt, numberFmt, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Minus, UtensilsCrossed, Truck, Wallet, Printer, CheckCircle2, Package, Search } from "lucide-react";

const STATUS = {
  ordered: { label: "Dipesan", cls: "bg-amber-100 text-amber-700" },
  picked_up: { label: "Diambil", cls: "bg-blue-100 text-blue-700" },
  settled: { label: "Lunas/Disetor", cls: "bg-emerald-100 text-emerald-700" },
};

export default function FoodOrders() {
  const { user } = useAuth();
  const { effectiveBranch } = useBranch();
  const isReseller = user.role === "reseller";

  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState({});
  const [menuSearch, setMenuSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [recap, setRecap] = useState(null);
  const [mperf, setMperf] = useState(null);
  const [mpStart, setMpStart] = useState(todayISO().slice(0, 8) + "01");
  const [mpEnd, setMpEnd] = useState(todayISO());

  const loadMperf = useCallback(() => {
    if (!isReseller) client.get("/reports/mitra-performance", { params: { ...(effectiveBranch ? { branch_id: effectiveBranch } : {}), start: mpStart, end: mpEnd } }).then((r) => setMperf(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [isReseller, effectiveBranch, mpStart, mpEnd]);
  useEffect(() => { loadMperf(); }, [loadMperf]);

  const bp = effectiveBranch ? { branch_id: effectiveBranch } : {};
  const loadOrders = useCallback(() => {
    client.get("/food-orders", { params: isReseller ? {} : bp }).then((r) => setOrders(r.data));
    if (!isReseller) client.get("/food-orders/recap", { params: bp }).then((r) => setRecap(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [effectiveBranch, isReseller]);

  const loadMenu = useCallback(() => {
    if (isReseller) client.get("/food-orders/availability").then((r) => setMenu(r.data.map((x) => ({ id: x.product_id, name: x.name, price: x.price, remaining: x.remaining }))));
  }, [isReseller]);
  useEffect(() => { loadMenu(); loadOrders(); }, [loadMenu, loadOrders]);

  const setQty = (id, d) => setCart((c) => {
    const m = menu.find((x) => x.id === id);
    let n = Math.max(0, (c[id] || 0) + d);
    if (d > 0 && m && m.remaining != null && n > m.remaining) { toast.error(`Kuota ${m.name} hari ini tersisa ${m.remaining} porsi`); return c; }
    const cp = { ...c }; if (n) cp[id] = n; else delete cp[id]; return cp;
  });
  const setQtyDirect = (id, val) => setCart((c) => {
    const m = menu.find((x) => x.id === id);
    let n = Math.max(0, Math.floor(Number(val) || 0));
    if (m && m.remaining != null && n > m.remaining) { toast.error(`Kuota ${m.name} hari ini tersisa ${m.remaining} porsi`); n = m.remaining; }
    const cp = { ...c }; if (n) cp[id] = n; else delete cp[id]; return cp;
  });
  const filteredMenu = useMemo(() => menu.filter((m) => !menuSearch || m.name.toLowerCase().includes(menuSearch.toLowerCase())), [menu, menuSearch]);
  const cartTotal = useMemo(() => Object.entries(cart).reduce((s, [id, q]) => s + q * (menu.find((m) => m.id === id)?.price || 0), 0), [cart, menu]);

  const submitOrder = async () => {
    const items = Object.entries(cart).map(([product_id, qty]) => ({ product_id, qty }));
    if (!items.length) { toast.error("Pilih minimal 1 menu"); return; }
    try {
      await client.post("/food-orders", { items, ...(isReseller ? {} : { branch_id: effectiveBranch }) });
      toast.success("Pesanan dibuat. Diambil besok, setor sore harinya."); setCart({}); loadOrders(); loadMenu();
    } catch (e) { toast.error(apiError(e)); }
  };

  const printSettlement = (o) => {
    const rows = o.items.map((i) => `<tr><td style="padding:4px 8px;border-bottom:1px solid #eee">${i.name}</td><td style="padding:4px 8px;text-align:center;border-bottom:1px solid #eee">${i.qty}</td><td style="padding:4px 8px;text-align:right;border-bottom:1px solid #eee">${rupiah(i.subtotal)}</td></tr>`).join("");
    const w = window.open("", "_blank", "width=420,height=640");
    w.document.write(`<html><head><title>Nota ${o.number}</title></head><body style="font-family:'Courier New',monospace;padding:18px;color:#111;max-width:320px">
      <div style="text-align:center"><div style="font-size:18px;font-weight:bold">TOKO KU</div><div>NOTA SETORAN MITRA</div></div>
      <div style="border-top:1px dashed #999;margin:8px 0"></div>
      <div>No: ${o.number}</div><div>Mitra: ${o.reseller_name}</div>
      <div>Pesan: ${o.order_date} · Ambil: ${o.pickup_date}</div>
      <div>Setor: ${o.settled_at ? new Date(o.settled_at).toLocaleString("id-ID") : "-"}</div>
      <div style="border-top:1px dashed #999;margin:8px 0"></div>
      <table style="width:100%;font-size:12px;border-collapse:collapse"><thead><tr><th style="text-align:left">Menu</th><th>Qty</th><th style="text-align:right">Subtotal</th></tr></thead><tbody>${rows}</tbody></table>
      <div style="border-top:1px dashed #999;margin:8px 0"></div>
      <div style="display:flex;justify-content:space-between;font-weight:bold"><span>TOTAL</span><span>${rupiah(o.total)}</span></div>
      <div style="display:flex;justify-content:space-between"><span>DISETOR</span><span>${rupiah(o.settlement_amount)}</span></div>
      <div style="display:flex;justify-content:space-between"><span>SELISIH</span><span>${rupiah(o.selisih)}</span></div>
      <div style="border-top:1px dashed #999;margin:8px 0"></div>
      <div style="text-align:center;font-size:11px">Diterima oleh: ${o.settled_by || "-"}<br/>Terima kasih 🙏</div>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const orderedList = useMemo(() => orders.filter((o) => o.status === "ordered"), [orders]);

  const printOrdered = () => {
    const list = orderedList;
    if (!list.length) { toast.error("Tidak ada pesanan berstatus Dipesan"); return; }
    const agg = {};
    list.forEach((o) => o.items.forEach((i) => {
      if (!agg[i.name]) agg[i.name] = { qty: 0, subtotal: 0 };
      agg[i.name].qty += i.qty; agg[i.name].subtotal += i.subtotal;
    }));
    const grandTotal = list.reduce((s, o) => s + o.total, 0);
    const totalPorsi = Object.values(agg).reduce((s, a) => s + a.qty, 0);
    const summaryRows = Object.entries(agg).sort((a, b) => b[1].qty - a[1].qty).map(([name, a]) =>
      `<tr><td style="padding:6px 10px;border-bottom:1px solid #eee">${name}</td><td style="padding:6px 10px;text-align:center;border-bottom:1px solid #eee;font-weight:bold">${a.qty}</td><td style="padding:6px 10px;text-align:right;border-bottom:1px solid #eee">${rupiah(a.subtotal)}</td></tr>`).join("");
    const detailBlocks = list.map((o) => {
      const rows = o.items.map((i) => `<tr><td style="padding:4px 10px;border-bottom:1px solid #f0f0f0">${i.name}</td><td style="padding:4px 10px;text-align:center;border-bottom:1px solid #f0f0f0">${i.qty}</td><td style="padding:4px 10px;text-align:right;border-bottom:1px solid #f0f0f0">${rupiah(i.subtotal)}</td></tr>`).join("");
      return `<div style="margin-bottom:14px;border:1px solid #e5e5e5;border-radius:8px;overflow:hidden">
        <div style="background:#f8fafc;padding:8px 12px;display:flex;justify-content:space-between;font-size:13px">
          <div><b>${o.reseller_name}</b> · <span style="font-family:monospace">${o.number}</span> ${o.prepared ? '<span style="color:#059669;font-weight:bold">✓ Sudah Disiapkan</span>' : '<span style="color:#b45309">belum disiapkan</span>'}</div>
          <div>Ambil: ${o.pickup_date}</div>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:12px"><tbody>${rows}</tbody>
          <tfoot><tr><td colspan="2" style="padding:6px 10px;text-align:right;font-weight:bold">Total</td><td style="padding:6px 10px;text-align:right;font-weight:bold">${rupiah(o.total)}</td></tr></tfoot>
        </table>
      </div>`;
    }).join("");
    const w = window.open("", "_blank", "width=800,height=900");
    w.document.write(`<html><head><title>Laporan Pesanan Siap Saji</title></head>
      <body style="font-family:Arial,Helvetica,sans-serif;padding:28px;color:#111;max-width:720px;margin:0 auto">
        <div style="text-align:center;margin-bottom:6px"><div style="font-size:22px;font-weight:bold">TOKO KU</div><div style="font-size:14px;color:#555">Laporan Pesanan Makanan Siap Saji</div></div>
        <div style="text-align:center;font-size:12px;color:#777;margin-bottom:16px">Status: Dipesan · Dicetak ${new Date().toLocaleString("id-ID")} · ${list.length} pesanan</div>
        <h3 style="font-size:15px;margin:0 0 6px">Rekap Total Menu yang Harus Disiapkan</h3>
        <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:8px">
          <thead><tr style="background:#111;color:#fff"><th style="padding:8px 10px;text-align:left">Menu</th><th style="padding:8px 10px;text-align:center">Total Porsi</th><th style="padding:8px 10px;text-align:right">Nilai</th></tr></thead>
          <tbody>${summaryRows}</tbody>
          <tfoot><tr style="background:#f1f5f9;font-weight:bold"><td style="padding:8px 10px">TOTAL (${totalPorsi} porsi)</td><td></td><td style="padding:8px 10px;text-align:right">${rupiah(grandTotal)}</td></tr></tfoot>
        </table>
        <div style="border-top:2px dashed #ccc;margin:20px 0"></div>
        <h3 style="font-size:15px;margin:0 0 10px">Rincian per Mitra</h3>
        ${detailBlocks}
        <div style="text-align:center;font-size:11px;color:#999;margin-top:20px">— TOKO KU —</div>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };

  const act = async (o, kind) => {
    try {
      await client.post(`/food-orders/${o.id}/${kind}`, kind === "settle" ? {} : undefined);
      toast.success(kind === "pickup" ? "Pesanan diambil" : "Setoran tercatat (barang habis)"); loadOrders();
    } catch (e) { toast.error(apiError(e)); }
  };

  const togglePrepare = async (o) => {
    try {
      await client.post(`/food-orders/${o.id}/prepare`, { prepared: !o.prepared });
      toast.success(!o.prepared ? "Ditandai sudah disiapkan" : "Penanda siap dibatalkan"); loadOrders();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 space-y-6 animate-in-up">
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pesanan Makanan Siap Saji</h1>
          <p className="text-sm text-muted-foreground">{isReseller ? "Pesan hari ini · ambil besok · setor sore (barang pasti habis)" : "Pantau & rekap pesanan mitra, catat setoran pembayaran"}</p>
        </div>
        {!isReseller && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={printOrdered} data-testid="print-food-orders"><Printer className="mr-2 h-4 w-4" /> Cetak Pesanan (PDF)</Button>
            <Button variant="outline" onClick={() => window.print()} data-testid="print-food-recap"><Printer className="mr-2 h-4 w-4" /> Cetak Rekap</Button>
          </div>
        )}
      </div>

      {isReseller && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold"><UtensilsCrossed className="h-5 w-5 text-primary" /> Pilih Menu</h3>
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={menuSearch} onChange={(e) => setMenuSearch(e.target.value)} placeholder="Cari menu siap saji..." className="pl-9" data-testid="menu-search" />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {filteredMenu.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`menu-${m.id}`}>
                  <div>
                    <div className="text-sm font-medium">{m.name}</div>
                    <div className="font-mono text-xs text-primary">{rupiah(m.price)}</div>
                    {m.remaining != null && <div className={`text-[11px] ${m.remaining <= 0 ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>sisa {m.remaining} porsi hari ini</div>}
                  </div>
                  <div className="flex items-center rounded-md border">
                    <button className="flex h-8 w-8 items-center justify-center hover:bg-slate-100" onClick={() => setQty(m.id, -1)} data-testid={`menu-minus-${m.id}`}><Minus className="h-4 w-4" /></button>
                    <Input type="number" min="0" value={cart[m.id] || 0} onChange={(e) => setQtyDirect(m.id, e.target.value)} className="h-8 w-14 rounded-none border-0 border-x text-center font-mono text-sm focus-visible:ring-0 focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`menu-qty-${m.id}`} />
                    <button className="flex h-8 w-8 items-center justify-center hover:bg-slate-100 disabled:opacity-30" disabled={m.remaining != null && (cart[m.id] || 0) >= m.remaining} onClick={() => setQty(m.id, 1)} data-testid={`menu-plus-${m.id}`}><Plus className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {!filteredMenu.length && <div className="col-span-full py-6 text-center text-sm text-muted-foreground">{menu.length ? "Menu tidak ditemukan" : "Belum ada menu siap saji"}</div>}
            </div>
          </Card>
          <Card className="flex flex-col p-5">
            <h3 className="font-heading text-lg font-semibold">Ringkasan Pesanan</h3>
            <div className="mt-2 flex-1 space-y-1 text-sm">
              {Object.entries(cart).map(([id, q]) => { const m = menu.find((x) => x.id === id); return <div key={id} className="flex justify-between"><span>{m?.name} ×{q}</span><span className="font-mono">{rupiah(q * (m?.price || 0))}</span></div>; })}
              {!Object.keys(cart).length && <div className="text-muted-foreground">Belum ada item dipilih</div>}
            </div>
            <div className="mt-3 flex justify-between border-t pt-3 text-lg font-bold"><span>Total Setoran</span><span className="font-mono text-primary" data-testid="food-cart-total">{rupiah(cartTotal)}</span></div>
            <Button className="mt-3 h-11" onClick={submitOrder} data-testid="submit-food-order">Buat Pesanan</Button>
          </Card>
        </div>
      )}

      {!isReseller && recap && (
        <div id="receipt-print" className="grid grid-cols-2 gap-4 lg:grid-cols-4 print:block">
          <Card className="p-4"><div className="text-sm text-muted-foreground">Total Pesanan</div><div className="font-mono text-xl font-bold">{numberFmt(recap.count)}</div></Card>
          <Card className="p-4"><div className="text-sm text-muted-foreground">Nilai Pesanan</div><div className="font-mono text-xl font-bold">{rupiah(recap.total_ordered)}</div></Card>
          <Card className="p-4"><div className="text-sm text-muted-foreground">Sudah Disetor</div><div className="font-mono text-xl font-bold text-emerald-600">{rupiah(recap.total_settled)}</div></Card>
          <Card className="p-4"><div className="text-sm text-muted-foreground">Status</div><div className="text-xs">Dipesan {recap.by_status.ordered} · Diambil {recap.by_status.picked_up} · Lunas {recap.by_status.settled}</div></Card>
        </div>
      )}

      {!isReseller && mperf && (
        <Card className="p-5 print:hidden">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-heading text-lg font-semibold">Performa Mitra</h3>
            <div className="flex items-end gap-2">
              <Input type="date" value={mpStart} onChange={(e) => setMpStart(e.target.value)} className="w-36" data-testid="mp-start" />
              <Input type="date" value={mpEnd} onChange={(e) => setMpEnd(e.target.value)} className="w-36" data-testid="mp-end" />
            </div>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Mitra</TableHead><TableHead className="text-right">Pesanan</TableHead><TableHead className="text-right">Nilai Pesanan</TableHead><TableHead className="text-right">Disetor</TableHead><TableHead className="text-right">Belum Lunas</TableHead></TableRow></TableHeader>
            <TableBody>
              {mperf.rows.map((r) => (
                <TableRow key={r.name} data-testid={`mperf-${r.name}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-right font-mono">{numberFmt(r.orders)}</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(r.total_ordered)}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-600">{rupiah(r.total_settled)}</TableCell>
                  <TableCell className="text-right font-mono text-amber-600">{rupiah(r.outstanding)}</TableCell>
                </TableRow>
              ))}
              {!mperf.rows.length && <TableRow><TableCell colSpan={5} className="py-6 text-center text-muted-foreground">Tidak ada data pada periode ini</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}

      <div>
        <h2 className="mb-3 font-heading text-xl font-semibold">{isReseller ? "Pesanan Saya" : "Semua Pesanan Mitra"}</h2>
        <div className="rounded-lg border bg-white">
          <Table>
            <TableHeader><TableRow>
              <TableHead>No</TableHead>{!isReseller && <TableHead>Mitra</TableHead>}
              <TableHead>Dipesan</TableHead><TableHead>Ambil</TableHead><TableHead>Item</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} className="hover:bg-slate-50" data-testid={`food-order-${o.id}`}>
                  <TableCell className="font-mono text-xs">{o.number}</TableCell>
                  {!isReseller && <TableCell className="text-sm">{o.reseller_name}</TableCell>}
                  <TableCell className="text-xs">{dateFmt(o.order_date)}</TableCell>
                  <TableCell className="text-xs">{dateFmt(o.pickup_date)}</TableCell>
                  <TableCell className="text-xs">{o.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(o.total)}{o.status === "settled" && o.selisih ? <span className={`block text-[10px] ${o.selisih < 0 ? "text-rose-600" : "text-emerald-600"}`}>setor {rupiah(o.settlement_amount)}</span> : null}</TableCell>
                  <TableCell>
                    <Badge className={STATUS[o.status]?.cls}>{STATUS[o.status]?.label}</Badge>
                    {o.status === "ordered" && o.prepared && <Badge className="ml-1 bg-emerald-100 text-emerald-700" data-testid={`prepared-badge-${o.id}`}><CheckCircle2 className="mr-1 h-3 w-3" />Siap</Badge>}
                  </TableCell>
                  <TableCell>
                    {o.status === "ordered" && !isReseller && (
                      <Button size="sm" variant={o.prepared ? "default" : "outline"} className={o.prepared ? "mr-2 bg-emerald-600 hover:bg-emerald-700" : "mr-2"} onClick={() => togglePrepare(o)} data-testid={`prepare-${o.id}`}>
                        {o.prepared ? <CheckCircle2 className="mr-1 h-3 w-3" /> : <Package className="mr-1 h-3 w-3" />}
                        {o.prepared ? "Sudah Disiapkan" : "Tandai Siap"}
                      </Button>
                    )}
                    {o.status === "ordered" && !isReseller && <Button size="sm" variant="outline" onClick={() => act(o, "pickup")} data-testid={`pickup-${o.id}`}><Truck className="mr-1 h-3 w-3" /> Ambil</Button>}
                    {o.status === "picked_up" && !isReseller && <Button size="sm" onClick={() => act(o, "settle")} data-testid={`settle-${o.id}`}><Wallet className="mr-1 h-3 w-3" /> Setor</Button>}
                    {o.status === "picked_up" && isReseller && <span className="text-xs text-muted-foreground">Menunggu setoran admin</span>}
                    {o.status === "settled" && <Button size="sm" variant="outline" onClick={() => printSettlement(o)} data-testid={`nota-${o.id}`}><Printer className="mr-1 h-3 w-3" /> Nota</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {!orders.length && <TableRow><TableCell colSpan={isReseller ? 7 : 8} className="py-8 text-center text-muted-foreground">Belum ada pesanan</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
